import { type ApiErrorBody, ApiErrorSchema } from "./schemas.js";

export class ApiClientError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly correlationId: string;
  readonly details: Readonly<Record<string, unknown>> | undefined;
  readonly body: ApiErrorBody | undefined;

  constructor(
    message: string,
    init: {
      statusCode: number;
      code: string;
      correlationId?: string;
      details?: Readonly<Record<string, unknown>>;
      body?: ApiErrorBody;
    },
  ) {
    super(message);
    this.name = "ApiClientError";
    this.statusCode = init.statusCode;
    this.code = init.code;
    this.correlationId = init.correlationId ?? "";
    this.details = init.details;
    this.body = init.body;
  }

  get isConflict(): boolean {
    return (
      this.statusCode === 409 ||
      this.code === "conflict" ||
      this.code === "stale_version" ||
      this.code === "instructor_conflict" ||
      this.code === "venue_capacity"
    );
  }
}

export function parseApiError(status: number, payload: unknown): ApiClientError {
  const parsed = ApiErrorSchema.safeParse(payload);
  if (parsed.success) {
    const err = parsed.data.error;
    return new ApiClientError(err.message, {
      statusCode: err.statusCode || status,
      code: err.code,
      correlationId: err.correlationId,
      ...(err.details ? { details: err.details } : {}),
      body: parsed.data,
    });
  }
  return new ApiClientError(`Request failed with status ${status}`, {
    statusCode: status,
    code: "http_error",
  });
}

export function isStaleOrConflict(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError && error.isConflict;
}
